import React, { useState, useEffect } from 'react';
import { baseUrl, assetFullLabel } from '../../shared';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';

import { STATUS_LABELS, JOB_TYPE_LABELS } from './index';
import { StatusChip } from '../../components/ui';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const FOLLOW_UP_TYPES = ['CALL', 'EMAIL', 'QUOTE_REVISED', 'OTHER'];
const FOLLOW_UP_LABELS = { CALL: 'Phone Call', EMAIL: 'Email', QUOTE_REVISED: 'Quote Revised', OTHER: 'Other' };

function AddFollowUpDialog({ open, jobId, onClose, onSaved }) {
  const [form, setForm] = useState({ followUpDate: '', followUpType: 'CALL', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ followUpDate: dayjs().format('YYYY-MM-DD'), followUpType: 'CALL', notes: '' });
  }, [open]);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    if (!form.followUpDate || !form.followUpType) return;
    setSaving(true);
    try {
      const res = await fetch(baseUrl + 'workshop/jobs/' + jobId + '/followups', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { onSaved(); onClose(); }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Log Follow-Up Action</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField size="small" fullWidth label="Follow-Up Date" type="date" value={form.followUpDate} onChange={set('followUpDate')} InputLabelProps={{ shrink: true }} />
          <FormControl fullWidth size="small">
            <InputLabel>Follow-Up Type</InputLabel>
            <Select value={form.followUpType} label="Follow-Up Type" onChange={set('followUpType')}>
              {FOLLOW_UP_TYPES.map(t => <MenuItem key={t} value={t}>{FOLLOW_UP_LABELS[t]}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" fullWidth multiline rows={3} label="Notes" value={form.notes} onChange={set('notes')} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !form.followUpDate}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FollowUpHistory({ jobId, canManage, refreshTrigger }) {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(baseUrl + 'workshop/jobs/' + jobId + '/followups', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setFollowUps(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [jobId, refreshTrigger]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this follow-up?')) return;
    await fetch(baseUrl + 'workshop/jobs/' + jobId + '/followups/' + id, { method: 'DELETE', headers: authHeaders() });
    setFollowUps(prev => prev.filter(f => f.id !== id));
  };

  if (loading) return <CircularProgress size={16} sx={{ ml: 2 }} />;
  if (followUps.length === 0) return <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>No follow-ups logged yet.</Typography>;

  return (
    <Box sx={{ ml: 2, mt: 0.5 }}>
      {followUps.map(f => (
        <Box key={f.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#0e407c' }}>
              {dayjs(f.followUpDate).format('DD/MM/YYYY')} — {FOLLOW_UP_LABELS[f.followUpType] || f.followUpType}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>by {f.createdBy}</Typography>
            {f.notes && <Typography variant="body2" sx={{ fontSize: 12, mt: 0.25 }}>{f.notes}</Typography>}
          </Box>
          {canManage && (
            <IconButton size="small" onClick={() => handleDelete(f.id)} sx={{ mt: -0.5 }}>
              <DeleteIcon fontSize="small" sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  );
}

function JobRow({ job, canManage, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [followUpRefresh, setFollowUpRefresh] = useState(0);

  const daysSinceStatus = job.updatedAt ? dayjs().diff(dayjs(job.updatedAt), 'day') : null;
  const targetDays = job.daysToQuote || 14;
  const isOverdue = daysSinceStatus !== null && daysSinceStatus > targetDays;

  const handleFollowUpSaved = () => {
    setFollowUpRefresh(r => r + 1);
    onRefresh();
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', bgcolor: isOverdue ? '#fff8f0' : undefined }}
        onClick={() => setExpanded(e => !e)}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isOverdue && (
              <Tooltip title={`${daysSinceStatus} days — over ${targetDays}-day target`}>
                <WarningAmberIcon fontSize="small" color="warning" />
              </Tooltip>
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.customer || '—'}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">{assetFullLabel(job) || '—'}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{JOB_TYPE_LABELS[job.jobType] || job.jobType}</Typography>
          <Typography variant="caption" color="text.secondary">{job.item || ''}</Typography>
        </TableCell>
        <TableCell>
          <StatusChip domain="job" value={job.status} label={STATUS_LABELS[job.status] || job.status} />
        </TableCell>
        <TableCell>
          {job.quotedExGst != null
            ? <Typography variant="body2">${Number(job.quotedExGst).toFixed(2)}</Typography>
            : <Typography variant="caption" color="text.secondary">—</Typography>
          }
        </TableCell>
        <TableCell>
          {daysSinceStatus !== null
            ? <Typography variant="body2" sx={{ color: isOverdue ? 'error.main' : 'text.primary', fontWeight: isOverdue ? 700 : 400 }}>{daysSinceStatus}d</Typography>
            : '—'
          }
          <Typography variant="caption" color="text.secondary"> / {targetDays}d target</Typography>
        </TableCell>
        <TableCell>
          {job.lastFollowUpDate
            ? <Typography variant="body2">{dayjs(job.lastFollowUpDate).format('DD/MM/YYYY')}</Typography>
            : <Typography variant="caption" color="text.secondary">None</Typography>
          }
          {job.followUpCount > 0 && (
            <Typography variant="caption" color="text.secondary"> ({job.followUpCount} logged)</Typography>
          )}
        </TableCell>
        <TableCell align="right" onClick={e => e.stopPropagation()}>
          {canManage && (
            <Tooltip title="Log follow-up action">
              <IconButton size="small" onClick={() => setAddOpen(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small">{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, border: expanded ? undefined : 'none' }}>
          <Collapse in={expanded}>
            <Box sx={{ py: 1.5, px: 2, bgcolor: '#fafafa' }}>
              <Box sx={{ display: 'flex', gap: 4, mb: 1, flexWrap: 'wrap' }}>
                {job.quoteNo && <Typography variant="caption">Quote: <strong>{job.quoteNo}</strong></Typography>}
                {job.customerPO && <Typography variant="caption">PO: <strong>{job.customerPO}</strong></Typography>}
                {job.jobAssignedTo && <Typography variant="caption">Assigned: <strong>{job.jobAssignedTo}</strong></Typography>}
                {job.dateReceived && <Typography variant="caption">Received: <strong>{dayjs(job.dateReceived).format('DD/MM/YYYY')}</strong></Typography>}
                {job.manufacturer && <Typography variant="caption">Make: <strong>{job.manufacturer} {job.model || ''}</strong></Typography>}
              </Box>
              {job.defect && <Typography variant="caption" color="text.secondary">Notes: {job.defect}</Typography>}
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 10 }}>Follow-Up History</Typography>
              <FollowUpHistory jobId={job.id} canManage={canManage} refreshTrigger={followUpRefresh} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <AddFollowUpDialog open={addOpen} jobId={job.id} onClose={() => setAddOpen(false)} onSaved={handleFollowUpSaved} />
    </>
  );
}

export default function NonApprovals() {
  const navigate = useNavigate();
  const canManage = localStorage.getItem('canManageWorkshop') === '1';

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetch(baseUrl + 'workshop/non-approvals', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setJobs(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const overdueCount = jobs.filter(j => {
    const days = j.updatedAt ? dayjs().diff(dayjs(j.updatedAt), 'day') : 0;
    return days > (j.daysToQuote || 14);
  }).length;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/workshop')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Pending Approval</Typography>
          <Typography variant="body2" color="text.secondary">Jobs awaiting customer approval</Typography>
        </Box>
        {overdueCount > 0 && (
          <Chip icon={<WarningAmberIcon />} label={`${overdueCount} overdue`} color="warning" sx={{ fontWeight: 700 }} />
        )}
        <Chip label={`${jobs.length} jobs`} variant="outlined" />
      </Box>

      {loading && <CircularProgress size={28} />}

      {!loading && jobs.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">No jobs currently awaiting approval.</Typography>
        </Box>
      )}

      {!loading && jobs.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer / Asset</TableCell>
              <TableCell>Job Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Quoted (Ex GST)</TableCell>
              <TableCell>Days Since Update</TableCell>
              <TableCell>Last Follow-Up</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map(job => (
              <JobRow key={job.id} job={job} canManage={canManage} onRefresh={load} />
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
